import { useState, useMemo, useEffect } from 'react';
import {
  Clipboard,
  PlusCircle,
  RefreshCw,
  LayoutGrid,
  Table as TableIcon,
  X,
  Search,
  Filter,
  Building2,
} from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { ptBR } from 'date-fns/locale';
import {
  format,
  startOfMonth,
  endOfMonth,
  subMonths,
} from 'date-fns';
import { AnimatePresence } from 'framer-motion';

import ButtonWithTooltip from '@/components/ButtonWithTooltip';
import { TypographyH1, TypographyMuted } from '@/components/ui/typography';
import CardsResearch from './components/CardsResearch';
import TableResearch from './components/TableResearch';
import AddDialog from './components/AddDialog';
import { useMarketResearchQuery } from '@/queries/useMarketResearchQuery';
import { useResaleQuery } from '@/queries/useResaleQuery';
import { FetchMarketResearchPayload } from '@/api/business/marketResearch';
import TopMessage from '@/components/TopMessage';
import { useBaseStore } from '@/store';
import { Store } from '@/store/type';
import { DebouncedInput } from '@/components/DebouncedInput';
import { MultiSelect } from '@/components/MultiSelect';
import { DatePickerWithRange } from '@/components/DatePickerWithRange';
import { Button } from '@/components/ui/button';
import { useNavigate, useSearchParams } from 'react-router-dom';

const stateSelector = (state: Store) => ({
  profile: state.authSlice.state.profile,
});

const MarketResearchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Filter form states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedResales, setSelectedResales] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(() => {
    const statusParam = searchParams.get('status');
    return statusParam ? [statusParam] : [];
  });
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const now = new Date();
    return {
      from: startOfMonth(subMonths(now, 1)),
      to: endOfMonth(now),
    };
  });

  // Applied payload state sent to API
  const [appliedPayload, setAppliedPayload] =
    useState<FetchMarketResearchPayload>(() => {
      const now = new Date();
      const statusParam = searchParams.get('status');
      return {
        SK_TEMPO_INICIO: Number(
          format(startOfMonth(subMonths(now, 1)), 'yyyyMMdd'),
        ),
        SK_TEMPO_FIM: Number(format(endOfMonth(now), 'yyyyMMdd')),
        DD_STATUS: statusParam ? [statusParam] : undefined,
      };
    });

  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam) {
      setSelectedStatuses([statusParam]);
      setAppliedPayload((prev) => ({
        ...prev,
        DD_STATUS: [statusParam],
      }));
    }
  }, [searchParams]);

  const { profile } = useBaseStore(stateSelector);
  const { data: resales } = useResaleQuery();
  const { data, isLoading, isFetching, refetch } =
    useMarketResearchQuery(appliedPayload);

  const onAddDialogClose = () => {
    setIsAddDialogOpen(false);
  };

  // Revenda options from master data
  const resaleOptions = useMemo(() => {
    if (!resales) return [];
    return resales.map((r) => ({
      value: String(r.SK_REVENDA),
      label: r.DS_REVENDA,
    }));
  }, [resales]);

  const handleToggleResale = (val: string) => {
    setSelectedResales((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    );
  };

  const handleSelectAllResales = (values: string[]) => {
    setSelectedResales((prev) => (prev.length === values.length ? [] : values));
  };

  const handleToggleStatus = (val: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val],
    );
  };

  const handleSelectAllStatuses = (values: string[]) => {
    setSelectedStatuses((prev) =>
      prev.length === values.length ? [] : values,
    );
  };

  const handleSearch = () => {
    const p: FetchMarketResearchPayload = {};
    if (searchTerm.trim()) {
      p.SEARCH_TERM = searchTerm.trim();
    }
    if (selectedResales.length > 0) {
      p.SK_REVENDAS = selectedResales.map(Number);
    }
    if (selectedStatuses.length > 0) {
      p.DD_STATUS = selectedStatuses;
    }
    if (dateRange?.from) {
      p.SK_TEMPO_INICIO = Number(format(dateRange.from, 'yyyyMMdd'));
    }
    if (dateRange?.to) {
      p.SK_TEMPO_FIM = Number(format(dateRange.to, 'yyyyMMdd'));
    }
    setAppliedPayload(p);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedResales([]);
    setSelectedStatuses([]);
    setDateRange(undefined);
    setAppliedPayload({});
  };

  const filteredData = data || [];

  const hasActiveFilters =
    Boolean(appliedPayload.SEARCH_TERM) ||
    Boolean(
      appliedPayload.SK_REVENDAS && appliedPayload.SK_REVENDAS.length > 0,
    ) ||
    Boolean(
      appliedPayload.DD_STATUS &&
      (Array.isArray(appliedPayload.DD_STATUS)
        ? appliedPayload.DD_STATUS.length > 0
        : true),
    ) ||
    Boolean(appliedPayload.SK_TEMPO_INICIO) ||
    Boolean(appliedPayload.SK_TEMPO_FIM);

  return (
    <div className="flex flex-col h-full overflow-auto p-4 bg-gray-50 space-y-4">
      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex justify-center items-center w-10 h-10 rounded-lg bg-primary/10">
            <Clipboard className="w-5 h-5 text-primary" />
          </div>
          <div>
            <TypographyH1 className="text-2xl font-semibold">
              Planejamento de Pesquisa
            </TypographyH1>
            <TypographyMuted>Acompanhe e efetue suas pesquisas</TypographyMuted>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {['ADMINISTRADOR', 'ADMINISTRADOR_REVENDA'].includes(profile!) && (
            <ButtonWithTooltip
              className="flex items-center justify-center  rounded-md"
              tooltipContent={<p>Cobertura por Revenda</p>}
              onClick={() => navigate('/market-research/reseller-coverage')}
              size="default"
            >
              <Building2 size={18} />
            </ButtonWithTooltip>
          )}
          {/* View Mode Toggle */}
          <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm mr-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              className="h-8 px-3 text-xs gap-1.5"
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid size={15} />
              Cards
            </Button>
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              className="h-8 px-3 text-xs gap-1.5"
              onClick={() => setViewMode('table')}
            >
              <TableIcon size={15} />
              Tabela
            </Button>
          </div>

          {['ADMINISTRADOR', 'ADMINISTRADOR_REVENDA'].includes(profile!) && (
            <ButtonWithTooltip
              className="flex items-center justify-center  rounded-md"
              tooltipContent={<p>Adicionar</p>}
              onClick={() => setIsAddDialogOpen(true)}
              size="default"
            >
              <PlusCircle size={18} />
            </ButtonWithTooltip>
          )}
          <ButtonWithTooltip
            tooltipContent={<p className="text-white">Atualizar</p>}
            variant="ghost"
            onClick={() => refetch()}
            className="shadow bg-white"
          >
            <RefreshCw size={18} />
          </ButtonWithTooltip>
        </div>
      </div>

      {/* Loading notifications */}
      <AnimatePresence>
        {isLoading && (
          <TopMessage
            text="Carregando dados iniciais..."
            variant="loading"
            className="text-green-500 bg-green-100"
          />
        )}
        {isFetching && !isLoading && (
          <TopMessage
            text="Consultando os dados..."
            variant="loading"
            className="text-primary-500 bg-blue-100"
          />
        )}
      </AnimatePresence>

      {/* Filters Bar */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm space-y-4">
        <div className="flex items-center justify-between pb-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Filter size={16} className="text-primary" />
            <span>Filtros de Pesquisa</span>
          </div>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearFilters}
              className="text-xs text-gray-500 hover:text-red-600 gap-1 h-7 px-2"
            >
              <X size={14} />
              Limpar Filtros
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          {/* Search Input */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
              Palavra-chave
            </label>
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <DebouncedInput
                value={searchTerm}
                onChange={(val) => setSearchTerm(String(val))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                placeholder="Nome ou código..."
                className="pl-9 bg-white text-xs h-10 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          {/* Resale MultiSelect */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
              Revendas (
              {selectedResales.length > 0 ? selectedResales.length : 'Todas'})
            </label>
            <MultiSelect
              label="Todas as Revendas"
              placeholder="Buscar revenda..."
              emptyLabel="Nenhuma revenda encontrada"
              options={resaleOptions}
              selectedValues={selectedResales}
              onChange={handleToggleResale}
              onSelectAll={handleSelectAllResales}
            />
          </div>

          {/* Status MultiSelect */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
              Status (
              {selectedStatuses.length > 0 ? selectedStatuses.length : 'Todos'})
            </label>
            <MultiSelect
              label="Todos os Status"
              placeholder="Buscar status..."
              emptyLabel="Nenhum status encontrado"
              options={[
                { value: 'Rascunho', label: 'Rascunho' },
                { value: 'Publicado', label: 'Publicado' },
                { value: 'Encerrado', label: 'Encerrado' },
              ]}
              selectedValues={selectedStatuses}
              onChange={handleToggleStatus}
              onSelectAll={handleSelectAllStatuses}
            />
          </div>

          {/* Date Range Filter */}
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
              Período
            </label>
            <DatePickerWithRange
              date={dateRange}
              setDate={setDateRange}
              locale={ptBR}
              className="w-full bg-white text-xs h-10"
            />
          </div>

          {/* Search Action Button */}
          <div className="flex gap-2">
            <Button
              variant="default"
              onClick={handleSearch}
              disabled={isFetching}
              className="w-full h-10 gap-2 text-xs bg-primary text-white hover:bg-primary/90 shadow-sm font-semibold"
            >
              <Search size={14} className={isFetching ? 'animate-spin' : ''} />
              <span>{isFetching ? 'Buscando...' : 'Buscar'}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Content view */}
      {filteredData.length === 0 && !isLoading ? (
        <div className="flex flex-col items-center justify-center p-12 bg-white rounded-xl border border-gray-200 text-center space-y-3">
          <Clipboard className="w-12 h-12 text-gray-300" />
          <p className="text-base font-medium text-gray-600">
            Nenhuma pesquisa encontrada
          </p>
          <p className="text-sm text-gray-400">
            Tente ajustar os filtros aplicados para encontrar os registros.
          </p>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={handleClearFilters}>
              Limpar Filtros
            </Button>
          )}
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredData.map((research, index) => (
            <CardsResearch key={index} data={research} />
          ))}
        </div>
      ) : (
        <div className="w-full flex-1 min-h-[500px] flex flex-col bg-white rounded-xl border border-gray-200 p-2 overflow-hidden">
          <TableResearch
            data={filteredData}
            isLoading={isLoading}
            isFetching={isFetching}
          />
        </div>
      )}

      <AddDialog onClose={onAddDialogClose} isOpen={isAddDialogOpen} />
    </div>
  );
};

export default MarketResearchPage;
